import { z } from 'zod';

/**
 * How a notification reaches somebody.
 *
 * `email` arrived exactly as `03-notifications.md` promised it would — one enum
 * member plus a delivery adapter (P21e1), appended **last** because the pg enum
 * was created in `push, inApp` order and a value added to an existing type is
 * appended: keeping the array in the same order keeps a freshly created database
 * sorting the column the way a migrated one does. `sms` followed the same way
 * (P30b1), and is carried only by direct `auth.*` codes — never a preference.
 *
 * `inApp` is not a lesser `push`. It is the durable one: a push may be refused
 * by the OS, dropped by FCM or never permitted at all, while the in-app row is
 * written by the api and is what the member's notification list — and the badge
 * — are counted from.
 */
export const NOTIFICATION_CHANNELS = ['push', 'inApp', 'email', 'sms'] as const;

export const NotificationChannelSchema = z.enum(NOTIFICATION_CHANNELS);

export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

const CHANNEL_SET: ReadonlySet<string> = new Set(NOTIFICATION_CHANNELS);

export function isNotificationChannel(value: string): value is NotificationChannel {
  return CHANNEL_SET.has(value);
}

/**
 * The channels a member may switch on or off, per category.
 *
 * A **listed** constant, deliberately not derived from {@link NOTIFICATION_CHANNELS}:
 * whether a channel is a member's choice is a decision taken here, once. A
 * one-time code carried only by direct `auth.*` notices travels on a channel
 * that is never a preference, and deriving this list would have offered it as
 * one the day the enum grew.
 */
export const PREFERENCE_CHANNELS = ['push', 'inApp', 'email'] as const;

export const PreferenceChannelSchema = z.enum(PREFERENCE_CHANNELS);

export type PreferenceChannel = (typeof PREFERENCE_CHANNELS)[number];

const PREFERENCE_CHANNEL_SET: ReadonlySet<string> = new Set(PREFERENCE_CHANNELS);

export function isPreferenceChannel(value: string): value is PreferenceChannel {
  return PREFERENCE_CHANNEL_SET.has(value);
}
